import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatErr, inr } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import LocationPicker from "@/components/LocationPicker";
import { payWithRazorpay } from "@/lib/razorpay";

export default function Checkout() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const payload = useMemo(() => {
    const o = {};
    const NUM = ["vehicles", "adults", "children", "rooms"];
    params.forEach((v, k) => { o[k] = NUM.includes(k) ? Number(v) : v; });
    return { kind: "safari", ...o };
  }, [params]);

  const [quote, setQuote] = useState(null);
  const [quoteErr, setQuoteErr] = useState("");
  const [guest, setGuest] = useState({ guest_name: "", guest_email: "", guest_mobile: "", pickup_location: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const [loc, setLoc] = useState({ pickup_location_id: "", drop_location_id: "", other: false, pickup_text: "" });
  const [promoIn, setPromoIn] = useState("");
  const [promo, setPromo] = useState("");
  const full = useMemo(() => ({
    ...payload,
    ...(promo ? { promo_code: promo } : {}),
    ...(loc.pickup_location_id ? { pickup_location_id: loc.pickup_location_id } : {}),
    ...(loc.drop_location_id ? { drop_location_id: loc.drop_location_id } : {}),
  }), [payload, promo, loc.pickup_location_id, loc.drop_location_id]);
  const applyPromo = async () => {
    const code = promoIn.trim().toUpperCase();
    if (!code) return;
    try { await api.post("/bookings/quote", { ...full, promo_code: code }); setPromo(code); toast.success(`Promo ${code} applied`); }
    catch (e) { toast.error(formatErr(e)); }
  };

  useEffect(() => {
    api.post("/bookings/quote", full)
      .then((r) => { setQuote(r.data); setQuoteErr(""); })
      .catch((e) => setQuoteErr(formatErr(e)));
  }, [full]);

  const needsPickup = payload.kind !== "stay";
  const [online, setOnline] = useState(false);
  const [agreed, setAgreed] = useState(false);
  useEffect(() => { api.get("/").then((r) => setOnline(r.data.payment_mode === "live")).catch(() => {}); }, []);

  const submit = async (e, payLater = false) => {
    e?.preventDefault();
    if (!guest.guest_name || !guest.guest_email || !guest.guest_mobile) {
      return toast.error("Please fill name, email and mobile");
    }
    if (!agreed) return toast.error("Please accept the Terms of Service and Cancellation Policy");
    setSubmitting(true);
    try {
      const { data: booking } = await api.post("/bookings", { ...full, ...guest, pickup_location: loc.other ? loc.pickup_text : "", pay_later: payLater });
      if (booking.payment?.order_id && !booking.payment.pay_later) {
        payWithRazorpay(booking, (data) => nav(`/booking-success?ref=${booking.booking_ref}`, { state: { voucher_token: data.voucher_token, email: data.guest_email } }));
      } else {
        toast.success(`Booking reserved · ${booking.booking_ref}`);
        nav(`/booking-success?ref=${booking.booking_ref}`, { state: { pending: true, email: booking.guest_email, online, hold: booking.hold_expires_at } });
      }
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (quoteErr) return (
    <div className="jt-container py-20" data-testid="checkout-error">
      <p className="text-[#C86D51] mb-3">{quoteErr}</p>
      <a className="jt-pill-outline" href="/">Start over</a>
    </div>
  );

  return (
    <div className="jt-container py-14 grid lg:grid-cols-3 gap-10">
      <form onSubmit={submit} className="lg:col-span-2 bg-white rounded-2xl p-8 ring-1 ring-black/5" data-testid="checkout-form">
        <div className="jt-eyebrow mb-2">Step 2 of 3</div>
        <h1 className="font-serif text-3xl mb-6 text-[#1E3A2B]">Guest details</h1>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Full name" required>
            <Input value={guest.guest_name} onChange={(e) => setGuest({ ...guest, guest_name: e.target.value })} data-testid="checkout-name"/>
          </Field>
          <Field label="Email" required>
            <Input type="email" value={guest.guest_email} onChange={(e) => setGuest({ ...guest, guest_email: e.target.value })} data-testid="checkout-email"/>
          </Field>
          <Field label="Mobile" required>
            <Input value={guest.guest_mobile} onChange={(e) => setGuest({ ...guest, guest_mobile: e.target.value })} data-testid="checkout-mobile"/>
          </Field>
          {needsPickup && <LocationPicker value={loc} onChange={setLoc}/>}
          <div className="md:col-span-2">
            <Field label="Notes (dietary, special requests)">
              <Textarea rows={3} value={guest.notes} onChange={(e) => setGuest({ ...guest, notes: e.target.value })} data-testid="checkout-notes"/>
            </Field>
          </div>
        </div>
        <div className="mt-6 flex gap-2 items-center flex-wrap" data-testid="checkout-promo">
          {promo ? (
            <span className="text-sm text-[#1E3A2B]" data-testid="checkout-promo-applied">Promo <strong>{promo}</strong> applied · <button type="button" onClick={() => { setPromo(""); setPromoIn(""); }} className="underline text-[#C86D51]" data-testid="checkout-promo-remove">remove</button></span>
          ) : (<>
            <input className="h-10 rounded-md border border-[#E2DACF] bg-white px-3 text-sm uppercase w-44" placeholder="Promo code" value={promoIn} onChange={(e) => setPromoIn(e.target.value)} data-testid="checkout-promo-input"/>
            <button type="button" onClick={applyPromo} className="jt-pill-outline text-sm" data-testid="checkout-promo-apply">Apply</button>
          </>)}
        </div>
        <label className="mt-6 flex items-start gap-3 text-sm text-[#3B4A42] cursor-pointer" data-testid="checkout-terms">
          <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5" data-testid="checkout-terms-checkbox"/>
          <span>I agree to the <Link to="/policies/terms" target="_blank" className="text-[#C86D51] underline" data-testid="checkout-terms-link">Terms of Service</Link> and <Link to="/policies/cancellation" target="_blank" className="text-[#C86D51] underline" data-testid="checkout-cancel-link">Cancellation Policy</Link>.</span>
        </label>
        <div className="mt-5 flex flex-wrap gap-3 items-center">
          <button className="jt-pill-primary disabled:opacity-40" disabled={submitting || !agreed} data-testid="checkout-submit">
            {submitting ? "Processing…" : online ? `Pay ${quote ? inr(quote.amount) : ""} now` : `Reserve now · ${quote ? inr(quote.amount) : ""}`}
          </button>
          {online && (
            <button type="button" onClick={(e) => submit(e, true)} className="jt-pill-outline disabled:opacity-40" disabled={submitting || !agreed} data-testid="checkout-pay-later">
              Reserve &amp; pay later · 2h hold
            </button>
          )}
        </div>
        <p className="text-xs text-[#5C6B63] mt-3 flex items-center gap-1" data-testid="checkout-pay-note"><ShieldCheck size={13}/>
          {online ? "Pay now by UPI, card, netbanking or wallet (secured by Razorpay), or reserve and pay within 2 hours." : "Pay later: we hold your jeep for 2 hours and contact you on WhatsApp to collect payment."}
        </p>
      </form>

      <aside className="bg-[#F5F0EB] rounded-2xl p-6 h-fit lg:sticky lg:top-24">
        <h3 className="font-serif text-xl mb-4 text-[#1E3A2B]">Your trip</h3>
        {quote ? (
          <>
            <img src={quote.image_url} alt="" className="rounded-xl aspect-video object-cover mb-4"/>
            <div className="text-sm space-y-2" data-testid="checkout-summary">
              {quote.summary.map(([k, v]) => <Row key={k} k={k} v={v}/>)}
            </div>
            <hr className="my-4 border-[#E2DACF]"/>
            <div className="flex items-baseline justify-between">
              <span className="text-[#5C6B63]">Total</span>
              <span className="font-serif text-2xl text-[#1E3A2B]" data-testid="checkout-total">{inr(quote.amount)}</span>
            </div>
            <p className="text-xs text-[#5C6B63] mt-2" data-testid="checkout-hold-note">{online ? "Live price · held 15 minutes while you pay now, or 2 hours if you reserve." : "Live price · held for 2 hours while our team collects payment."}</p>
          </>
        ) : <div className="text-sm text-[#5C6B63]">Checking availability…</div>}
      </aside>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="jt-eyebrow mb-2 block">{label}{required && <span className="text-[#C86D51]">*</span>}</Label>
      {children}
    </div>
  );
}
function Row({ k, v }) {
  return <div className="flex justify-between"><span className="text-[#5C6B63]">{k}</span><span className="font-medium text-right">{v}</span></div>;
}
